import { HttpErrorResponse } from '@angular/common/http';

const EXPIRED_OR_INVALID_TOKEN = /token.*\b(expired|invalid)\b|\b(expired|invalid)\b.*token/i;

/**
 * True when a REST failure means our token is no longer accepted. A 403 is only treated as
 * such when it says so: otherwise it is a genuine permission denial and the session stands.
 */
export function isHttpAuthFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  if (error.status === 401) {
    return true;
  }
  return error.status === 403 && EXPIRED_OR_INVALID_TOKEN.test(bodyMessage(error.error));
}

function bodyMessage(body: unknown): string {
  if (typeof body === 'string') {
    return body;
  }
  if (body && typeof body === 'object') {
    const { message, error } = body as { message?: unknown; error?: unknown };
    return [message, error].filter(part => typeof part === 'string').join(' ');
  }
  return '';
}

interface GraphqlErrorLike {
  errorType?: string;
  originalError?: { name?: string; $metadata?: { httpStatusCode?: number } };
}

/**
 * True when an AppSync failure means our token is no longer accepted. AppSync rejects the
 * whole request with a 401 `UnauthorizedException`; a field-level `Unauthorized` error is a
 * genuine permission denial and must not end the session.
 */
export function isGraphqlAuthFailure(error: unknown): boolean {
  const errors = (error as { errors?: unknown } | null)?.errors;
  if (!Array.isArray(errors)) {
    return false;
  }
  return errors.some((e: GraphqlErrorLike) =>
    e?.errorType === 'UnauthorizedException' ||
    e?.originalError?.name?.startsWith('UnauthorizedException') === true ||
    e?.originalError?.$metadata?.httpStatusCode === 401
  );
}
